import {
  Dropdown,
  Table,
  TableOperationHeader,
  Typography,
} from '@getsynapse/design-system';
import ExportIcon from 'assets/icons/export.svg';
import classnames from 'classnames';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { get, isNaN } from 'lodash';
import AttachmentsSidePanel from 'Pages/BudgetsPage/tabs/Plan/components/sidepanels/AttachmentsSidePanel';
import NoteSidePanel from 'Pages/BudgetsPage/tabs/Plan/components/sidepanels/NoteSidePanel';
import {
  calculateParentActualCell,
  calculateParentAllocationCell,
  monthIndecesForYearToDate,
} from 'Pages/BudgetsPage/tabs/Report/helper/reportHelpers';
import { getBudgetFiscalYear } from 'Pages/BudgetsPage/helpers/budgetHelper';
import React, { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCurrentProjectData } from 'state/Project/projectSlice';
import MoreActionsPopup, {
  OptionProps,
} from '../../../../Organisms/TableHeaderActionButtons/MoreActionsPopup';
import TableSkeletonLoader from '../../../../Organisms/TableSkeletonLoader/TableSkeletonLoader';
import {
  getProjectAllBudgetInfo,
  getProjectLineItemStatus,
  updateLineItemActualForProject,
  updateLineItemNoteForProject,
} from '../../../../state/BudgetList/budgetListSlice';
import { getLDTeams } from '../../../../state/Teams/teamsSlice';
import { getVendors } from '../../../../state/Vendors/vendorsSlice';
import { EDIT_BUDGET_TABS, SLICE_STATUS } from '../../../../utils/constants';
import {
  ActualTimes,
  UpdateLineItemActualsRequest,
} from '../../../../utils/constants/budget';
import {
  BudgetModel,
  LineItemWithAmounts,
  Option,
  TimeFrame,
  TimeOptionsEnum,
} from '../../../../utils/customTypes';
import { ParentOfLineItems } from '../../../../utils/types/budget';
import useBudgetFilters from '../../../BudgetsPage/hooks/useBudgetFilters';
import { generateMultipleReportRows } from '../../../BudgetsPage/tabs/Report/helper/reportTableHelpers';
import BudgetSummaryView from './components/BudgetSummaryView';
import RenderNoRecords from '../../../../Atoms/NoRecords/NoRecords';
import emptyIcon from 'assets/images/empty-project-budget.svg';

const BudgetDetailsPage = () => {
  const [parentLineItems, setParentLineItems] = useState<ParentOfLineItems[]>(
    []
  );

  const tableActionsList: Partial<OptionProps>[] = [
    {
      label: intl.get('EXPORT'),
      selectOption: () => {},
      iconSrc: ExportIcon,
    },
  ];

  const dispatch = useDispatch();
  const projectAllBudgets = useSelector(getProjectAllBudgetInfo);
  const [projectActiveBudget, setProjectActiveBudget] = useState<BudgetModel>();
  const [projectActiveBudgetLineItemList, setProjectActiveBudgetLineItemList] =
    useState<LineItemWithAmounts[]>([]);

  const [selectedTime, setSeletedTime] = useState<Option>({
    label: TimeOptionsEnum.FISCAL_YEAR + ' ' + new Date().getFullYear(),
    value: ActualTimes.FISCAL_CURRENT,
  });
  const [timeOptions, setTimeOptions] = useState<Option[]>([]);
  const [activeTimeframe, setActiveTimeframe] = useState<Option>();
  let [totalAmount, setTotalAmount] = useState<number>(0);
  let [totalActual, setTotalActual] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<string>();
  const [monthsForYearToDate, setMonthsForYearToDate] = useState<number[]>([]);
  const [shouldDisplayNoteSidePanel, setShouldDisplayNoteSidePanel] =
    useState<boolean>(false);
  const [storedLineItem, setStoredLineItem] = useState<string>();
  const [storedNoteData, setStoredNoteData] =
    useState<LineItemWithAmounts | null>(null);
  const [displayAttachmentModal, setDisplayAttachmentModal] = useState(false);
  const projectData = useSelector(getCurrentProjectData);
  const { projectId } = useParams<{ projectId: string }>();

  useEffect(() => {
    let newTimeOptions: Option[] = [];

    // if no active budget, then create the time options for the fiscal year
    if (!projectActiveBudget) {
      projectAllBudgets.forEach((budgetAndLineItem) => {
        const year = getBudgetFiscalYear(
          budgetAndLineItem.budget.fiscalPeriodStart
        );
        const budgetMonthStart = new Date(
          budgetAndLineItem.budget.fiscalPeriodStart
        ).getUTCMonth();

        newTimeOptions.push({
          label: TimeOptionsEnum.FISCAL_YEAR + ' ' + year,
          value: ActualTimes.FISCAL_CURRENT + '_' + year,
        });

        let otherOptions: Option[] = [];

        switch (budgetAndLineItem.budget.timeFrame) {
          case TimeFrame.QUARTERLY:
            otherOptions = generateQuarterlyOptions(year);
            break;
          case TimeFrame.MONTHLY:
            otherOptions = generateMonthlyOptions(year, budgetMonthStart);
            break;
          default:
            break;
        }
        newTimeOptions = [...newTimeOptions, ...otherOptions];
      });
      setTimeOptions(newTimeOptions);
      setActiveTimeframe(newTimeOptions[0]);
    } else {
      // if there is an active budget, then update the line item list for the active budget
      const lineItemList = projectAllBudgets.find(
        (budgetAndLineItem) =>
          budgetAndLineItem.budget.id === projectActiveBudget?.id
      )?.lineItemAmounts;
      setProjectActiveBudgetLineItemList(lineItemList ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectAllBudgets]);

  // select budget and line items based on time frame
  useEffect(() => {
    if (activeTimeframe) {
      const [i, year] = activeTimeframe.value.split('_');
      const selectedTimeFrame: Option = {
        label: activeTimeframe.label,
        value: i,
      };
      setSeletedTime(selectedTimeFrame);
      if (year !== selectedYear) {
        setSelectedYear(year);
        projectAllBudgets.forEach((budgetAndLineItem) => {
          if (
            getBudgetFiscalYear(budgetAndLineItem.budget.fiscalPeriodStart) ===
            parseInt(year)
          ) {
            setProjectActiveBudget(budgetAndLineItem.budget);
            setProjectActiveBudgetLineItemList(
              budgetAndLineItem.lineItemAmounts
            );
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimeframe, selectedYear]);

  // calculate total amount and actual based on selected time frame and budget
  useEffect(() => {
    const fiscalMonthStart = new Date(
      projectActiveBudget?.fiscalPeriodStart || ''
    ).getUTCMonth();
    const monthsForYearToDate = monthIndecesForYearToDate(
      projectActiveBudget
        ? getBudgetFiscalYear(projectActiveBudget.fiscalPeriodStart)
        : new Date().getUTCFullYear(),
      projectActiveBudget
        ? new Date(projectActiveBudget.fiscalPeriodStart).getUTCMonth()
        : 0
    );

    setMonthsForYearToDate(monthsForYearToDate);

    if (parentLineItems.length) {
      setTotalAmount(
        calculateParentAllocationCell(
          parentLineItems[0],
          projectActiveBudget?.timeFrame || TimeFrame.QUARTERLY,
          fiscalMonthStart,
          selectedTime,
          monthsForYearToDate
        )
      );
      setTotalActual(
        calculateParentActualCell(
          parentLineItems[0],
          projectActiveBudget?.timeFrame || TimeFrame.QUARTERLY,
          fiscalMonthStart,
          selectedTime,
          monthsForYearToDate
        )
      );
    }
  }, [
    parentLineItems,
    projectActiveBudget,
    projectActiveBudgetLineItemList,
    selectedTime,
  ]);

  // calculate the parent line items for the active budget
  useEffect(() => {
    if (projectData) {
      let parent: ParentOfLineItems = {
        id: projectId,
        type: 'projectId',
        lineItems: projectActiveBudgetLineItemList,
        children: [],
        rawAmounts: Array(12).fill(0),
        rawActuals: Array(12).fill(0),
        title: projectData.title,
      };

      parent.rawAmounts = parent.lineItems!.reduce((sums: number[], item) => {
        item.rawAmounts.forEach((amount, index) => {
          sums[index] = (sums[index] || 0) + amount;
        });
        return sums;
      }, Array(12).fill(0));

      parent.rawActuals = parent.lineItems!.reduce((sums: number[], item) => {
        item.rawActuals.forEach((amount, index) => {
          sums[index] = (sums[index] || 0) + amount;
        });
        return sums;
      }, Array(12).fill(0));
      setParentLineItems([parent]);
    }
  }, [projectId, projectData, projectActiveBudgetLineItemList]);

  // generate the monthly options for the fiscal year
  const generateMonthlyOptions = (
    startYear: number,
    budgetMonthStart: number
  ) =>
    Array.from({ length: 12 }, (_, i) => {
      const monthIndex: number = (budgetMonthStart + i) % 12;
      const calendarYear =
        budgetMonthStart + i < 12 ? startYear : startYear + 1;
      return {
        label: `${months[monthIndex]} ${calendarYear}`,
        value: `${monthIndex}_${startYear}`,
      };
    });

  // generate the quarterly options for the fiscal year
  const generateQuarterlyOptions = (year: number) => {
    const quarters: (keyof typeof TimeOptionsEnum)[] = ['Q1', 'Q2', 'Q3', 'Q4'];
    return quarters.map((quarter, i) => ({
      label: `${TimeOptionsEnum[quarter]} ${year}`,
      value: `${i}_${year}`,
    }));
  };

  const onTimeframeChange = (option: { label: string; value: string }) => {
    setActiveTimeframe(option);
  };

  const lineItemStatus = useSelector(getProjectLineItemStatus);

  useEffect(() => {
    dispatch(getLDTeams());
    dispatch(getVendors());
  }, [dispatch]);

  const { programOptions, learningTeamOptions, vendorOptions, projectOptions } =
    useBudgetFilters();

  const [focusedCellId, setFocusedCellId] = useState<string>('');

  const {
    showNotification,
    isNotificationVisible,
    snackbarProps,
    SnackbarNotification,
  } = useSnackbarNotification();

  function handleAddNote(item: LineItemWithAmounts) {
    setStoredNoteData(item);
    setShouldDisplayNoteSidePanel(true);
  }

  const toggleNoteSidePanel = () =>
    setShouldDisplayNoteSidePanel((prevState) => !prevState);

  function handleAddAttachment(itemId: string) {
    setStoredLineItem(itemId);
    setDisplayAttachmentModal(true);
  }

  const onNoteSaved = async (note: string) => {
    const response = await dispatch(
      updateLineItemNoteForProject({
        id: storedNoteData!.id,
        note: note,
      })
    );
    if (get(response, 'payload.data')) {
      setShouldDisplayNoteSidePanel(false);
      showNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.NOTIFICATIONS.NOTE_UPDATE_SUCCESS',
          { title: note }
        ),
        hasActionButton: true,
      });
    } else if (get(response, 'error', null)) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR',
          { title: note }
        ),
        hasActionButton: true,
      });
    }
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  async function onEditLineItem(amount: number, lineItemId: string) {
    let updatedMonths: string[] = [];
    const index = parseFloat(selectedTime.value);

    const month = projectActiveBudget
      ? new Date(projectActiveBudget.fiscalPeriodStart).getUTCMonth()
      : 0;
    if (
      !isNaN(parseFloat(selectedTime.value)) &&
      isFinite(selectedTime.value)
    ) {
      switch (projectActiveBudget?.timeFrame) {
        case TimeFrame.MONTHLY:
          updatedMonths.push(months[index]);
          break;
        case TimeFrame.QUARTERLY:
          for (let i = 0; i < 3; i++) {
            updatedMonths.push(months[(index * 3 + i + month) % 12]);
          }

          break;
        case TimeFrame.YEARLY:
          updatedMonths = [...months];
          break;
        default:
          break;
      }
    } else if (selectedTime.value === ActualTimes.FISCAL_CURRENT) {
      updatedMonths = [...months];
    }

    let req: UpdateLineItemActualsRequest = {
      budgetId: projectActiveBudget!.id,
      lineItemId: lineItemId,
      actual: amount,
      months: updatedMonths,
    };

    dispatch(updateLineItemActualForProject(req));
  }

  return (
    <div data-cy='budget-details-tab' className='px-6'>
      <div className='flex items-center justify-between w-full'>
        <Typography variant='h4' className='text-left'>
          {intl.get('BUDGET.BUDGET_DETAILS_TABLE.TITLE')}
        </Typography>
        <div className='ml-auto'>
          {timeOptions.length > 0 && (
            <Dropdown
              options={timeOptions}
              onChange={onTimeframeChange}
              values={activeTimeframe ? [activeTimeframe] : []}
              className='w-46'
            />
          )}
        </div>
      </div>

      <BudgetSummaryView
        totalAmount={totalAmount}
        totalActual={totalActual}
        activeTimeframe={activeTimeframe}
      />
      <TableOperationHeader
        className='border-b-0'
        searchInputContainerClassName='z-5'
        hideSearch={true}
        actionButtons={
          <>
            <MoreActionsPopup options={tableActionsList} />
          </>
        }
      />
      {lineItemStatus === SLICE_STATUS.LOADING && <TableSkeletonLoader />}
      {lineItemStatus !== SLICE_STATUS.LOADING && (
        <div
          className={classnames('w-full rounded-b mb-8', {
            'overflow-auto border border-neutral-lighter-two ': true,
            'max-h-no-filtered-budget-table-body': true,
          })}
        >
          {projectActiveBudgetLineItemList.length > 0 ? (
            <Table
              numberOfHeadRows={2}
              canSelectRows={false}
              className='relative min-w-full border-0 custom-table-spacing'
              data={{
                headData: [
                  {
                    stickyHeader: true,
                    headCells: [
                      {
                        content: '',
                        className: classnames('h-10 w-40 left-0 z-2'),
                      },
                      {
                        content: `${selectedTime?.label}`,
                        className: 'text-center',
                        colSpan: 3,
                      },
                      selectedTime.value !== ActualTimes.FISCAL_CURRENT
                        ? {
                            content: (
                              <div>
                                {intl.get(
                                  'BUDGET_PAGE.REPORT.TABLE.HEAD.FISCAL_YEAR'
                                )}{' '}
                                {selectedYear}
                              </div>
                            ),
                            className: 'text-center',
                            colSpan: 3,
                          }
                        : null,
                      {
                        content: intl.get(
                          'BUDGET_PAGE.REPORT.TABLE.HEAD.DETAILS'
                        ),
                        className: 'text-center',
                        colSpan: 5,
                      },
                    ].filter(Boolean),
                  },
                  {
                    stickyHeader: true,
                    headCells: [
                      {
                        content: (
                          <div className='p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.PROJECT')}
                          </div>
                        ),
                        className: classnames('w-200px p-0 left-0 z-2'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get(
                              'BUDGET_PAGE.REPORT.TABLE.HEAD.ALLOCATED'
                            )}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.ACTUAL')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.VARIANCE')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      selectedTime.value !== ActualTimes.FISCAL_CURRENT
                        ? {
                            content: (
                              <div className='w-157px p-2'>
                                {intl.get(
                                  'BUDGET_PAGE.REPORT.TABLE.HEAD.ALLOCATED'
                                )}
                              </div>
                            ),
                            className: classnames('w-157px p-0'),
                          }
                        : null,
                      selectedTime.value !== ActualTimes.FISCAL_CURRENT
                        ? {
                            content: (
                              <div className='w-157px p-2'>
                                {intl.get(
                                  'BUDGET_PAGE.REPORT.TABLE.HEAD.ACTUAL'
                                )}
                              </div>
                            ),
                            className: classnames('w-157px p-0'),
                          }
                        : null,
                      selectedTime.value !== ActualTimes.FISCAL_CURRENT
                        ? {
                            content: (
                              <div className='w-157px p-2'>
                                {intl.get(
                                  'BUDGET_PAGE.REPORT.TABLE.HEAD.VARIANCE'
                                )}
                              </div>
                            ),
                            className: classnames('w-157px p-0'),
                          }
                        : null,
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.TEAM')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.PROGRAM')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get(
                              'BUDGET_PAGE.REPORT.TABLE.HEAD.BUDGET_SOURCE'
                            )}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.VENDOR')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      },
                      {
                        content: <div className='w-120px'></div>,
                        className: classnames('w-120px'),
                      },
                    ].filter(Boolean),
                  },
                ],
                rows: generateMultipleReportRows(
                  parentLineItems,
                  0,
                  {},
                  () => {},
                  {},
                  () => {},
                  {},
                  onEditLineItem,
                  programOptions,
                  projectOptions,
                  vendorOptions,
                  learningTeamOptions,
                  EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT,
                  projectActiveBudget
                    ? new Date(
                        projectActiveBudget.fiscalPeriodStart
                      ).getUTCMonth()
                    : 0,
                  monthsForYearToDate,
                  selectedTime,
                  0,
                  0,
                  projectActiveBudget?.timeFrame ?? TimeFrame.QUARTERLY,
                  focusedCellId,
                  setFocusedCellId,
                  true,
                  true,
                  handleAddNote,
                  handleAddAttachment
                ).flat(),
              }}
            />
          ) : (
            <RenderNoRecords
              dataCy='no-budget-found'
              caption={intl.get('PROJECT_DETAIL.NO_BUDGET_DATA')}
              imageSrc={emptyIcon}
              className='h-empty-filtered-table-body'
            />
          )}
        </div>
      )}

      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}

      <AttachmentsSidePanel
        open={displayAttachmentModal}
        lineItemId={storedLineItem}
        onClose={() => {
          setDisplayAttachmentModal(false);
        }}
      />

      <NoteSidePanel
        open={shouldDisplayNoteSidePanel}
        onClose={toggleNoteSidePanel}
        onNoteSaved={onNoteSaved}
        title={storedNoteData?.description ?? ''}
        note={storedNoteData?.note ?? ''}
      />
    </div>
  );
};

export default BudgetDetailsPage;
